import React, { useState, useEffect } from 'react';
import Card from "../../../core/Card";
import style from "../../../views/style.module.css";
import Input from "../../../core/Inputs/DefaultInput";
import SearchInput from '../../../core/Inputs/SearchInput';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const SurveyGroupModal = ({
    open,
    onSuccess,
    onCancel,
    groupData = null, // null for add mode, object for edit mode
    surveysList = [], // List of all available surveys
    selectedSurveyIds = [], // IDs of surveys already in the group (for edit mode)
    mode = 'create' // 'create', 'edit', or 'addToGroup'
}) => {
    const { t } = useTranslation();
    const [groupName, setGroupName] = useState('');
    const [nameError, setNameError] = useState(false);
    const [selectedSurveys, setSelectedSurveys] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // Determine mode
    const isEditMode = mode === 'edit' || (groupData && mode !== 'addToGroup');
    const isAddToGroupMode = mode === 'addToGroup' && groupData;
    const isCreateMode = !groupData && mode === 'create';

    // Initialize form when modal opens or groupData changes
    useEffect(() => {
        if (open) {
            if (isAddToGroupMode) {
                // Adding surveys to existing group - pre-select the surveys that are already selected in the table
                setGroupName(groupData.name || '');
                setSelectedSurveys(selectedSurveyIds || []);
            } else if (groupData) {
                // Edit mode - show existing group data
                setGroupName(groupData.name || '');
                setSelectedSurveys(selectedSurveyIds || []);
            } else {
                // Create mode
                setGroupName('');
                setSelectedSurveys(selectedSurveyIds || []);
            }
            setNameError(false);
            setSearchQuery('');
        }
    }, [open, groupData, selectedSurveyIds, isAddToGroupMode]);

    // Filter surveys based on search query
    const filteredSurveys = surveysList.filter(survey => {
        if (!searchQuery) return true;
        const surveyName = survey.survey_name
            ? (typeof survey.survey_name === 'string'
                ? JSON.parse(survey.survey_name)
                : survey.survey_name)
            : {};
        const name = Object.values(surveyName)[0] || '';
        return name.toLowerCase().includes(searchQuery.toLowerCase());
    });

    // Handle survey selection/deselection
    const handleSurveyToggle = (surveyId) => {
        const survey = surveysList.find(s => s.survey_id === surveyId);
        
        setSelectedSurveys(prev => {
            // If survey is already selected, allow deselection
            if (prev.includes(surveyId)) {
                return prev.filter(id => id !== surveyId);
            } 
            // If trying to select a new survey, check language constraint
            else {
                const newSurveyLanguage = survey?.su_languages?.toString() || '';
                
                // Check if a survey with the same language is already selected
                const hasLanguageConflict = prev.some(selectedId => {
                    const selectedSurvey = surveysList.find(s => s.survey_id === selectedId);
                    const selectedLanguage = selectedSurvey?.su_languages?.toString() || '';
                    return selectedLanguage === newSurveyLanguage && newSurveyLanguage !== '';
                });
                
                if (hasLanguageConflict) {
                    const languageName = getLanguageName(survey);
                    toast.error(t("survey.groups.errors.languageConflict"));
                    return prev; // Don't add the survey
                }
                
                return [...prev, surveyId];
            }
        });
    };

    // Handle select all with language validation
    const handleSelectAll = () => {
        if (selectedSurveys.length === filteredSurveys.length) {
            setSelectedSurveys([]);
        } else {
            // Select only one survey per language
            const selectedByLanguage = {};
            const validSelections = [];
            
            filteredSurveys.forEach(survey => {
                const languageCode = survey.su_languages?.toString() || '';
                
                // Only add if we haven't selected a survey for this language yet
                if (!selectedByLanguage[languageCode]) {
                    selectedByLanguage[languageCode] = true;
                    validSelections.push(survey.survey_id);
                }
            });
            
            setSelectedSurveys(validSelections);
            
            // Show info message if some surveys were skipped
            if (validSelections.length < filteredSurveys.length) {
                toast.info(t("survey.groups.toast.languageDuplicateSkipped"));
            }
        }
    };

    // Handle save
    const handleSave = () => {
        if (isAddToGroupMode) {
            // Just add the selected surveys to the group
            onSuccess({
                groupId: groupData.id,
                surveyIds: selectedSurveys,
                mode: 'addToGroup'
            });
        } else {
            // Create or edit group
            if (!groupName.trim()) {
                setNameError(true);
                return;
            }
            setNameError(false);
            onSuccess({
                name: groupName,
                surveyIds: selectedSurveys,
                groupId: groupData?.id || null,
                mode: isEditMode ? 'edit' : 'create'
            });
        }
    };

    // Handle cancel
    const handleCancel = () => {
        setGroupName('');
        setSelectedSurveys([]);
        setNameError(false);
        setSearchQuery('');
        onCancel();
    };

    // Get survey name for display
    const getSurveyName = (survey) => {
        try {
            const surveyNameObj = survey.survey_name
                ? (typeof survey.survey_name === 'string'
                    ? JSON.parse(survey.survey_name)
                    : survey.survey_name)
                : {};
            return Object.values(surveyNameObj)[0] || t("survey.groups.untitledSurvey");
        } catch (error) {
            return t("survey.groups.untitledSurvey");
        }
    };

    // Get language name for display
    const getLanguageName = (survey) => {
        // If language_name is available in the survey object, use it
        if (survey.language_name) {
            return survey.language_name;
        }
        
        // Fallback: Map of common language codes to language names
        const languageMap = {
            '22': 'English',
            '23': 'Spanish',
            '24': 'French',
            '25': 'German',
            '26': 'Italian',
            '27': 'Portuguese',
            '28': 'Chinese',
            '29': 'Japanese',
            '30': 'Korean',
            '31': 'Arabic',
            '32': 'Russian',
            '33': 'Dutch',
            '34': 'Swedish',
            '35': 'Norwegian',
            '36': 'Danish',
            '37': 'Finnish',
        };
        
        const langCode = survey.su_languages?.toString() || '';
        return languageMap[langCode] || `Language ${langCode}` || 'Unknown';
    };

    const allSelected = filteredSurveys.length > 0 && selectedSurveys.length === filteredSurveys.length;
    const someSelected = selectedSurveys.length > 0 && selectedSurveys.length < filteredSurveys.length;

    return (
        <>
            <div
                className={`customvar-modal modal custom-modal fade deleteModal ${open ? 'show' : ''}`}
                style={{ display: `${open ? 'block' : 'none'}` }}
                id="surveyGroupModal"
                tabIndex={-1}
                aria-labelledby="surveyGroupModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                        <div className="modal-body">
                            <div className={"formcontainer " + style.container}>
                                <div className={"formvawrapper delete-modal-wrap modal-setting-large " + style.wrapper}>
                                    <Card view="raised" className={"formstyle p-0 " + style.card}>
                                        <div className="delete-body-wrap delete-language input-margin-bottom">
                                            <div className="heading">
                                                <h3>
                                                    {isAddToGroupMode
                                                        ? `${t("survey.groups.addToGroup")} ${groupData?.name || t("survey.groups.untitledGroup")}`
                                                        : isEditMode
                                                            ? t("survey.groups.manageTitle")
                                                            : t("survey.groups.createTitle")}
                                                </h3>
                                            </div>

                                            {/* Group Name Input - hide for addToGroup mode */}
                                            {!isAddToGroupMode && (
                                                <div className="mb-3 mb-md-4 position-relative">
                                                    <label className="form-label-custom">{t("survey.groups.groupName")}</label>
                                                    <Input
                                                        type='text'
                                                        view='outlined'
                                                        dense={false}
                                                        color='default'
                                                        label=''
                                                        placeholder={t("survey.groups.enterGroupName")}
                                                        clearButton={true}
                                                        success={false}
                                                        error={nameError}
                                                        fill={true}
                                                        errorHelperText={null}
                                                        className={"formsinput"}
                                                        value={groupName}
                                                        onChange={(e) => {
                                                            setGroupName(e.target.value);
                                                            if (nameError && e.target.value.trim()) {
                                                                setNameError(false);
                                                            }
                                                        }}
                                                        onClear={() => {
                                                            setGroupName('');
                                                            setNameError(true);
                                                        }}
                                                    />
                                                    {nameError && <div className="validation-msg">{t("survey.groups.errors.nameRequired")}</div>}
                                                </div>
                                            )}

                                            {/* Survey Selection Section */}
                                            <div className="mb-3 mb-md-4">
                                                <label className="form-label-custom mb-2">{t("survey.groups.selectSurvey")}</label>

                                                {/* Search Input */}
                                                <div className="mb-3">
                                                    <SearchInput
                                                        className="custom-search-input survey-search-input"
                                                        type='def'
                                                        view='outlined'
                                                        color='primary'
                                                        placeholder={t("survey.search")}
                                                        fill={true}
                                                        onSearch={setSearchQuery}
                                                        onClear={() => setSearchQuery('')}
                                                    />
                                                </div>

                                                {/* Select All Checkbox */}
                                                {filteredSurveys.length > 0 && (
                                                    <div className="mb-2 pb-2 border-bottom">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <label className="d-flex align-items-center survey-select-all-label mb-0">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={allSelected}
                                                                    ref={(input) => {
                                                                        if (input) input.indeterminate = someSelected;
                                                                    }}
                                                                    onChange={handleSelectAll}
                                                                    className="me-2 mb-0"
                                                                />
                                                                <span>{t("survey.groups.selectAll")}</span> 
                                                                 <div className="text-muted" style={{ fontSize: '15px', marginTop: '0px', marginLeft: '20px' }}>
                                                            {t("survey.groups.languageRestriction")}
                                                        </div>
                                                            </label>
                                                            {/* {selectedSurveys.length > 0 && (
                                                                <div className="text-muted survey-selected-count">
                                                                    {selectedSurveys.length} survey{selectedSurveys.length !== 1 ? 's' : ''} selected
                                                                </div>
                                                            )} */}
                                                           
                                                        </div>
                                                        
                                                    </div>
                                                )}

                                                {/* Surveys List */}
                                                <div className="survey-selection-list">
                                                    {filteredSurveys.length === 0 ? (
                                                        <div className="text-center py-4 text-muted">
                                                            {searchQuery ? t("survey.variable.nodata") : t("survey.groups.noSurveysSelected")}
                                                        </div>
                                                    ) : (
                                                        filteredSurveys.map((survey) => {
                                                            const isSelected = selectedSurveys.includes(survey.survey_id);
                                                            const surveyLanguage = survey.su_languages?.toString() || '';
                                                            
                                                            // Check if another survey with the same language is already selected
                                                            const isLanguageAlreadySelected = !isSelected && selectedSurveys.some(selectedId => {
                                                                const selectedSurvey = surveysList.find(s => s.survey_id === selectedId);
                                                                const selectedLanguage = selectedSurvey?.su_languages?.toString() || '';
                                                                return selectedLanguage === surveyLanguage && surveyLanguage !== '';
                                                            });
                                                            
                                                            return (
                                                                <div
                                                                    key={survey.survey_id}
                                                                    className={`survey-item d-flex align-items-center py-2 ${isLanguageAlreadySelected ? 'opacity-50' : ''}`}
                                                                    onClick={() => !isLanguageAlreadySelected && handleSurveyToggle(survey.survey_id)}
                                                                    style={{ cursor: isLanguageAlreadySelected ? 'not-allowed' : 'pointer' }}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={() => handleSurveyToggle(survey.survey_id)}
                                                                        className="me-3 mb-0"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        disabled={isLanguageAlreadySelected}
                                                                    />
                                                                    <div className="flex-grow-1">
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <span className="surv-title">{getSurveyName(survey)}</span>
                                                                            <span className="text-muted" style={{ fontSize: '13px', color: '#999' }}>
                                                                                ({getLanguageName(survey)})
                                                                            </span>
                                                                            {isLanguageAlreadySelected && (
                                                                                <span className="text-muted" style={{ fontSize: '11px', color: '#E60000' }}>
                                                                                    - {t("survey.groups.languageAlreadySelected")}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {/* {survey.totalSection && survey.totalQuestion && (
                                                                            <div className="secondary-text">
                                                                                {survey.totalSection} Sections, {survey.totalQuestion} Questions
                                                                            </div>
                                                                        )} */}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>


                                            </div>

                                            {/* Action Buttons */}
                                            <div className="button-wrap d-flex justify-content-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={handleSave}
                                                >
                                                    {isAddToGroupMode ? t("survey.groups.addToGroup") : isEditMode ? t("survey.stages.update") : t("survey.groups.createBtn")}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-bordered ms-3"
                                                    onClick={handleCancel}
                                                >
                                                   {t('survey.customVariable.cancel')}
                                                </button>
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div
                className={`modal-backdrop fade ${open ? 'show' : ''}`}
                style={{ display: `${open ? 'block' : 'none'}` }}
                onClick={handleCancel}
            ></div>
        </>
    );
};

export default SurveyGroupModal;

